import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {fetchUserProfile, fetchUserActivity, fetchProfileRankings} from '../lib/api';
import {UserProfile, SOCIALS, ProfileRankingsResponse} from '../types/profile';
import ActivityHeatmap from '../components/ActivityHeatmap';
import '../styles/ranking.css';

const Profile: React.FC = () => {
  const params = useParams<{ username: string }>();
  const username = params.username || '';
  const decodedName = decodeURIComponent(username);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activity, setActivity] = useState<Record<string, number> | null>(null);
  const [rankings, setRankings] = useState<ProfileRankingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!decodedName) {
      setError('Unknown user');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        const [profileData, activityData] = await Promise.all([
          fetchUserProfile(decodedName),
          fetchUserActivity(decodedName).catch(() => ({})) // Activity failure shouldn't block profile
        ]);
        
        if (!cancelled) {
          setProfile(profileData);
          setActivity(activityData);
          console.log('profileData', profileData);
          console.log('profileData.user_id', profileData?.user_id);
          if (profileData && profileData.user_id) {
            console.log('Fetching rankings for user_id:', profileData.user_id);
            fetchProfileRankings(profileData.user_id.toString())
              .then(data => {
                  console.log('Fetched rankings:', data); // Log the fetched data
                  if (!cancelled) setRankings(data);
              })
              .catch(err => console.error('Failed to fetch profile rankings:', err));
          } else {
             console.log('No user_id found in profileData, skipping rankings fetch');
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Unable to load this profile at the moment. Please try again later.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [decodedName]);

  const socialLinks = useMemo(() => {
    if (!profile) return [];
    return SOCIALS.filter(({ key }) => {
      const handle = profile.social_platforms?.[key];
      return typeof handle === 'string' && handle.trim().length > 0;
    }).map((meta) => {
      const handle = profile.social_platforms?.[meta.key] as string;
      return {...meta, handle, url: `${meta.urlPrefix}${handle}`};
    });
  }, [profile]);

  const weeklyRankings = useMemo(() => {
    console.log('Calculating weeklyRankings', { rankings, profileId: profile?.user_id });
    if (!rankings || !profile?.user_id) return [];
      const userRankings = rankings[profile.user_id];
        console.log('userRankings', userRankings);
        if (!userRankings?.weekly_rankings) return [];
        
      return Object.entries(userRankings.weekly_rankings).map(([key, entry]) => {
        // key format: "2025-12-07_timed_1_min_easy" |  We want to extract the date
        const match = key.match(/^(\d{4}-\d{2}-\d{2})/);
        const dateStr = match ? match[1] : '';
        return {key, dateStr, ...entry};
      }).sort((a, b) => b.dateStr.localeCompare(a.dateStr)); // Sort by date descending
    }, [rankings, profile]);

  return (
    <div className="ranking-page">
      <h1 className="page-title">Player Profile</h1>
      <p className="page-subtitle">Player: <strong>{decodedName}</strong></p>
      
      {activity && <ActivityHeatmap data={activity} />}

      <div className="category-card profile-card">
        {profile?.description && (<div className="profile-description" style={{marginBottom: '20px', whiteSpace: 'pre-wrap'}}><p>{profile.description}</p></div>)}
        <h2 className="category-card-title">Social Media</h2>
        {loading && <p>Loading social links...</p>}
        {error && <p className="error-text">{error}</p>}
        {!loading && !error && (
          <>
            {socialLinks.length === 0 && (<p className="muted-text">This player has not shared any social media links yet.</p>)}
            {socialLinks.length > 0 && (
              <div className="social-links-grid">
                {socialLinks.map(({ key, label, url, handle }) => (
                  <a key={key} className="social-link-card" href={url} target="_blank" rel="noreferrer">
                  <span className="social-link-label">{label}</span>
                  <span className="social-link-handle">@{handle}</span>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Weekly Rankings Section */}
      {weeklyRankings.length > 0 && (
          <div className="category-card profile-card" style={{ marginTop: '20px' }}>
              <h2 className="category-card-title">Weekly Rankings History</h2>
              <div className="table-wrapper">
                <table className="ranking-table ranking-table-no-outer-border">
                    <thead>
                        <tr>
                            <th style={{textAlign: 'left', width: '25%'}}>Week Of</th>
                            <th style={{textAlign: 'left', width: '25%'}}>Category</th>
                            <th style={{textAlign: 'right', width: '25%'}}>Score</th>
                            <th style={{textAlign: 'right', width: '25%'}}>Rank</th>
                        </tr>
                    </thead>
                    <tbody>
                        {weeklyRankings.map((item) => (
                            <tr key={item.key}>
                                <td style={{textAlign: 'left'}}>{item.dateStr}</td>
                                <td style={{textAlign: 'left'}}>{item.ranking_type}</td>
                                <td style={{textAlign: 'right'}}>{item.score}</td>
                                <td style={{textAlign: 'right'}}>#{item.ranking}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
              </div>
          </div>
      )}
    </div>
  );
};

export default Profile;
